import React, { useState, useEffect } from "react";

/**
 * @typedef {Object} ClassificationConfig
 * @property {"dynamic" | "unclassified"} classification_type
 * @property {{ min: number, max: number } | undefined} [classification_extent] -
 */

/**
 * A form to configure data classification for chorpleth map visualization.
 *
 * @param {Object} props
 * @param {ClassificationConfig} props.classificationConfig
 * @param {(classificationConfig: ClassificationConfig) => void} onClassificationConfigChange
 * @returns {JSX.Element}
 */
function DataClassificationForm(_ref) {
  var _classificationConfig, _classificationConfig2;
  let {
    classificationConfig,
    onClassificationConfigChange
  } = _ref;
  const [errorMessage, setErrorMessage] = useState(undefined);
  useEffect(() => {
    const [, error] = validateClassificationConfig(classificationConfig);
    setErrorMessage(error);
  }, [classificationConfig]);
  const validateAndUpdate = updatedConfig => {
    const [, error] = validateClassificationConfig(updatedConfig);
    setErrorMessage(error);
    onClassificationConfigChange(updatedConfig);
  };
  const handleClassificationTypeChange = e => {
    const updatedConfig = {
      ...classificationConfig,
      classification_type: e.target.value
    };
    validateAndUpdate(updatedConfig);
  };
  const handleMinValueChange = e => {
    const updatedConfig = {
      ...classificationConfig,
      classification_extent: {
        ...classificationConfig.classification_extent,
        min: parseFloat(e.target.value) || 0
      }
    };
    validateAndUpdate(updatedConfig);
  };
  const handleMaxValueChange = e => {
    const updatedConfig = {
      ...classificationConfig,
      classification_extent: {
        ...classificationConfig.classification_extent,
        max: parseFloat(e.target.value) || 0
      }
    };
    validateAndUpdate(updatedConfig);
  };
  const classificationOptions = [{
    value: "dynamic",
    label: "Classification dynamique"
  }, {
    value: "unclassified",
    label: "Sans classes (échelle continue)"
  }];
  return /*#__PURE__*/React.createElement("div", {
    className: "card mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "card-body"
  }, /*#__PURE__*/React.createElement("h5", null, "Classification"), /*#__PURE__*/React.createElement("p", null, "Configurer la classification utilis\xE9e pour la repr\xE9sentation des donn\xE9es sous forme de carte choropl\xE8the."), /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "classificationType",
    className: "form-label"
  }, "Type de Classification"), /*#__PURE__*/React.createElement("select", {
    className: "form-select",
    id: "classificationType",
    value: classificationConfig.classification_type,
    onChange: handleClassificationTypeChange
  }, classificationOptions.map(option => /*#__PURE__*/React.createElement("option", {
    key: option.value,
    value: option.value
  }, option.label)))), classificationConfig.classification_type === "unclassified" ? /*#__PURE__*/React.createElement("p", null, "On utilise une \xE9chelle de couleur continue (pas de classification) entre les valeurs min et max d\xE9finies ci-dessous. L'\xE9chelle reste fixe au changement de filtre, territoire, ann\xE9e...") : /*#__PURE__*/React.createElement("p", null, "La classification (en 6 classes) est calcul\xE9e automatiquement en fonction des donn\xE9es pr\xE9sent\xE9es et selon la m\xE9thode s\xE9lectionn\xE9e (parmi quantile (par d\xE9faut), equidistant, logarithmique). L'\xE9chelle est donc recalcul\xE9e \xE0 chaque changement de filtre, territoire, ann\xE9e...", " "), classificationConfig.classification_type === "unclassified" && /*#__PURE__*/React.createElement("div", {
    className: "row g-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-md-6"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "minValue",
    className: "form-label"
  }, "Borne inferieure"), /*#__PURE__*/React.createElement("input", {
    type: "number",
    className: "form-control",
    id: "minValue",
    value: (_classificationConfig = classificationConfig.classification_extent) === null || _classificationConfig === void 0 ? void 0 : _classificationConfig.min,
    onChange: handleMinValueChange
  })), /*#__PURE__*/React.createElement("div", {
    className: "col-md-6"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "maxValue",
    className: "form-label"
  }, "Borne Superieure"), /*#__PURE__*/React.createElement("input", {
    type: "number",
    className: "form-control",
    id: "maxValue",
    value: (_classificationConfig2 = classificationConfig.classification_extent) === null || _classificationConfig2 === void 0 ? void 0 : _classificationConfig2.max,
    onChange: handleMaxValueChange
  }))), errorMessage && /*#__PURE__*/React.createElement("div", {
    className: "text-danger mt-3"
  }, errorMessage)));
}
export default DataClassificationForm;

/**
 * Validates the classification configuration.
 *
 * @param {ClassificationConfig} classificationConfig
 * @returns {[boolean, string | undefined]} Tuple of (isValid, errorMessage)
 */
export function validateClassificationConfig(classificationConfig) {
  const {
    classification_type,
    classification_extent
  } = classificationConfig;
  if (!["dynamic", "unclassified"].includes(classification_type)) {
    return [false, "Type de classification invalide."];
  }
  if (classification_type === "unclassified") {
    if (!classification_extent) {
      return [false, "L'étendue de classification est requise pour le type 'Sans classe (échelle continue)'."];
    }
    const {
      min,
      max
    } = classification_extent;
    if (min === undefined || max === undefined) {
      return [false, "Les valeurs min et max doivent être définies pour l'étendue de classification"];
    }
    if (min >= max) {
      return [false, "La valeur min doit être strictement inférieure à la valeur max."];
    }
  }
  return [true, undefined];
}